import { useCallback, useEffect, useRef, useState } from "react";
import { API, apiError } from "../lib/api";
import { Library, Download, Upload, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function MasterCatalog() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [q, setQ] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/master-catalog");
      setEntries(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const dl = async (path, filename) => {
    try {
      const res = await API.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await API.post("/master-catalog/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${data.inserted} entries` + (data.skipped ? ` · ${data.skipped} skipped` : ""));
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this entry from master catalog?")) return;
    try { await API.delete(`/master-catalog/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const reset = async () => {
    if (!window.confirm("Wipe the entire master catalog? Branch stock is NOT affected.")) return;
    try { const { data } = await API.post("/master-catalog/reset"); toast.success(`Wiped ${data.deleted} entries`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const filtered = entries.filter((e) =>
    !q.trim() ? true : `${e.name} ${e.company} ${e.composition_text}`.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-5" data-testid="master-catalog-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#10B981] to-[#047857] text-white flex items-center justify-center">
            <Library className="w-6 h-6" />
          </div>
          <div>
            <div className="overline mb-1">Admin · Master Catalog</div>
            <h1 className="font-display text-3xl font-bold">Medicine Master List</h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Upload a canonical list of medicine names (with optional company + salt composition).
              Any branch can then download this list pre-filled and only fill batch, expiry, MRP,
              purchase price and quantity — keeping naming and salts consistent across all branches.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => dl("/master-catalog/template", "master_catalog_template.xlsx")}
                  data-testid="mc-download-template"
                  className="btn-secondary">
            <Download className="w-4 h-4" /> Sample Template
          </button>
          <button onClick={() => dl("/master-catalog/download", `master_catalog.xlsx`)}
                  data-testid="mc-download-list"
                  disabled={entries.length === 0}
                  className="btn-secondary">
            <Download className="w-4 h-4" /> Download Full Catalog
          </button>
          <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden"
                 onChange={(e) => upload(e.target.files?.[0])} data-testid="mc-file-input" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
                  data-testid="mc-upload-btn"
                  className="btn-primary">
            <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload Master Catalog"}
          </button>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900">
        <b>How it works:</b>
        <ol className="list-decimal pl-5 mt-1.5 space-y-0.5">
          <li>Download the sample template. Fill only <b>name</b> (and optionally company + salt composition).</li>
          <li>Upload it here — becomes the master list every branch can see.</li>
          <li>Each branch opens <b>Stock → Bulk Import</b> and clicks <b>Download Master Catalog</b> to get a pre-filled Excel — they only fill expiry, price and quantity.</li>
        </ol>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
          <div className="font-display font-semibold">
            {entries.length} medicine{entries.length === 1 ? "" : "s"} in master catalog
          </div>
          <div className="flex items-center gap-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search master list…"
                   data-testid="mc-search"
                   className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm w-64" />
            {entries.length > 0 && (
              <button onClick={reset} data-testid="mc-reset-btn"
                      className="text-xs text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> Wipe catalog
              </button>
            )}
          </div>
        </div>
        {loading ? (
          <div className="p-10 text-sm text-slate-500 text-center">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-sm text-slate-500 text-center">
            {entries.length === 0
              ? "No master entries yet — upload an Excel to get started."
              : "No entries match your search."}
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[65vh]">
            <table className="data-table">
              <thead>
                <tr><th>Name</th><th>Company</th><th>Composition</th><th className="text-right pr-4">Actions</th></tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <tr key={e.id} data-testid={`mc-row-${e.id}`}>
                    <td className="font-medium">{e.name}</td>
                    <td className="text-slate-600">{e.company || "—"}</td>
                    <td className="text-xs text-slate-500 font-mono">{e.composition_text || "—"}</td>
                    <td className="text-right pr-4">
                      <button onClick={() => del(e.id)} className="p-1.5 rounded hover:bg-red-50 text-slate-500 hover:text-red-600"
                              data-testid={`mc-del-${e.id}`} title="Remove">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
